import { AxiosError, Method } from 'axios';
import { useCallback } from 'react';

import { toast } from '@servicetitan/anvil2';

import { IBaseResponse, IUiResponse } from '@fleet-pro/mobile-webview-api';

import { getErrorMessageTitle } from '../utils/error-message-title';
import { isAxiosCancellationError } from '../utils/is-cancellation-error';

export const useGlobalErrorInterceptor = () => {
    const globalErrorInterceptor = useCallback((error: AxiosError<IBaseResponse | IUiResponse>) => {
        if (!isAxiosCancellationError(error) && !error.config?.disableGlobalErrorHandling) {
            const method = error.config?.method;
            const url = error.config?.url ?? '';
            const { errors } = error.response?.data ?? {};
            const status = error.response?.status;

            if (status === 403) {
                toast.danger({
                    title: 'Action not allowed',
                    message: "You don't have permission to perform this action.",
                });
            } else {
                const errorArray = errors ?? [];
                // There is TS bug, see https://github.com/microsoft/TypeScript/issues/44373#issuecomment-1405744410 for the temporary fix
                const fixedErrors: (typeof errorArray)[number][] = errorArray;
                const normalizedUniqueErrorMessages = [
                    ...fixedErrors.reduce((errorMessages, error) => {
                        const trimmedErrorMessage = error.message?.trim();

                        if (trimmedErrorMessage) {
                            errorMessages.add(trimmedErrorMessage);
                        }

                        return errorMessages;
                    }, new Set<string>()),
                ];

                let message: string = 'Something went wrong.';

                if (Array.isArray(errors)) {
                    message = errorContentProcessor(normalizedUniqueErrorMessages);
                }

                toast.danger({
                    title: getErrorMessageTitle(method as Method, url),
                    message,
                });
            }
        }

        return Promise.reject(error);
    }, []);

    return { globalErrorInterceptor };
};

// A2 toast message is string-only, so join messages instead of JSX
const errorContentProcessor = (errors: string[]) => errors.join('\n');
